import { Button } from '@/components/ui/button'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Header from '@/components/Header'
import authBg from '@/assets/Landing/authbg.svg'
import { api } from '@/utils/api'

export default function SetPasswordPage() {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Get token from URL query params
    const tokenFromUrl = searchParams.get('token')
    const emailFromUrl = searchParams.get('email')
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl)
    } else {
      setError('Invalid or missing reset token')
    }
    
    if (emailFromUrl) {
      setEmail(emailFromUrl)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    if (!token) {
      setError('Reset token is required')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!isPasswordValid) {
      setError('Please meet all password requirements')
      return
    }

    setLoading(true)

    try {
      await api.post('/auth/reset-password', {
        token,
        password: newPassword,
        confirmPassword,
      })
      
      setMessage('Password set successfully! Redirecting to login...')
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      setError((err as Error)?.message || 'Failed to set password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  const passwordRequirements = {
    hasMinLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  }

  const isPasswordValid = Object.values(passwordRequirements).every(req => req)

  return (
    <div className="min-h-screen bg-white flex flex-col w-full">
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-rows-2 w-full">
        {/* Left Section - Form */}
        <div className="flex flex-col lg:flex-row items-center justify-center px-6 lg:px-16 py-12 lg:py-16 w-full">
          <div className="w-full space-y-8 flex flex-col lg:flex-row gap-8">
            <div className="space-y-3 flex flex-col justify-start items-start lg:w-1/2">
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900">
                Set Your Password
              </h2>
              <div className="space-y-1">
                <p className="text-gray-600 text-base lg:text-lg xl:text-xl text-start">
                  Welcome to IRIS! Please set your password to activate your organization admin account and continue.
                </p>
              </div>
            </div>

           <div className='flex flex-row items-center justify-center w-full lg:w-1/2'>
             <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-md">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-start">
                  {error}
                </div>
              )}
              {message && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-start">
                  {message}
                </div>
              )}
              
              {email && (
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 text-start">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    disabled
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-500 bg-gray-50"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 text-start">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 text-start">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>

              {/* Password Requirements */}
              {newPassword && (
                <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 text-start">Password Requirements:</p>
                  <ul className="space-y-1 text-sm text-start">
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`h-4 w-4 rounded-full flex items-center justify-center text-xs ${passwordRequirements.hasMinLength ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordRequirements.hasMinLength ? '✓' : '○'}
                      </span>
                      At least 8 characters
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`h-4 w-4 rounded-full flex items-center justify-center text-xs ${passwordRequirements.hasUppercase ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordRequirements.hasUppercase ? '✓' : '○'}
                      </span>
                      At least one uppercase letter
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`h-4 w-4 rounded-full flex items-center justify-center text-xs ${passwordRequirements.hasSpecialChar ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordRequirements.hasSpecialChar ? '✓' : '○'}
                      </span>
                      At least one special character
                    </li>
                  </ul>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-6 text-base font-semibold"
                disabled={loading || !token || !isPasswordValid}
              >
                {loading ? 'Setting Password...' : 'Set Password'}
              </Button>

              <p className="text-sm text-gray-600 text-center">
                Already have your password?{' '}
                <a href="/login" className="text-[#F97316] hover:text-[#EA580C] font-semibold">
                  Login here
                </a>
              </p>
            </form>
           </div>
          </div>
        </div>

        {/* Right Section - Image */}
        <div className="hidden lg:block relative h-full min-h-[400px] bg-gray-100">
          <img
            src={authBg}
            alt="Authentication background"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  )
}
